import React, { useState, useEffect } from 'react';
import {
  Code,
  TestTube,
  Database,
  Mail,
  Phone,
  MapPin,
  Download,
  Github,
  Linkedin,
  ExternalLink,
  Menu,
  X,
  Calendar,
  Image,
  Video,
  Globe,
  Send,
  Sun,
  Moon
} from 'lucide-react';

const Portfolio = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDarkTheme, setIsDarkTheme] = useState(true);
  const [emailData, setEmailData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isEmailSending, setIsEmailSending] = useState(false);

  // Set document title
  useEffect(() => {
    document.title = 'Magdy Portfolio';
  }, []);

  // Data
  const skills = {
    qa: [
      { name: 'Manual Testing', level: 95 },
      { name: 'Test Automation', level: 95 },
      { name: 'API Testing', level: 100 },
      { name: 'Agile Methodologies', level: 90 },
      { name: 'ISTQB Practices', level: 90 }
    ],
    automation: [
      { name: 'Robot Framework', level: 100 },
      { name: 'Selenium', level: 100 },
      { name: 'Cypress', level: 65 },
      { name: 'Appium', level: 100 },
      { name: 'Postman', level: 100 },
      { name: 'RestAssured', level: 100 },
      { name: 'Playwright', level: 55 },
      { name: 'Grafana k6', level: 70 }
    ],
    programming: [
      { name: 'Python', level: 100 },
      { name: 'JavaScript', level: 85 },
      { name: 'Java', level: 100 },
      { name: 'SQL', level: 90 },
      { name: 'C/C++', level: 75 }
    ],
    devops: [
      { name: 'Docker', level: 80 },
      { name: 'Jenkins', level: 75 },
      { name: 'Kubernetes', level: 75 },
      { name: 'AWS', level: 60 },
      { name: 'Git', level: 100 },
      { name: 'Linux', level: 100 }
    ],
    general: [
      { name: 'React', level: 80 },
      { name: 'MongoDB', level: 80 },
      { name: 'Express', level: 80 },
      { name: 'Axios', level: 70 },
      { name: 'Node.js', level: 70 },
    ]
  };

  const qaProjects = [

    {
      id: 1,
      title: 'iOS Automation Framework',
      description: 'BDD iOS automation suite using Appium and Behave for critical path validation',
      detailedDescription: 'Designed and implemented an iOS-specific mobile automation framework using Appium and Behave, focused on validating critical user journeys through BDD feature files. The framework enables direct interaction with real devices and simulators to simulate real-world user behavior. It supports structured step definitions, reusable page components, and automatic screenshot capture on failures. Additionally, it includes proxy integration to monitor backend API calls during test runs, facilitating seamless debugging and root cause analysis.',
      technologies: ['Appium', 'Behave', 'Python', 'iOS', 'Xcode', 'BDD'],
      features: [
        'Automated validation of critical user flows on iOS',
        'BDD-style feature files for clear business alignment',
        'Real device and simulator support',
        'Reusable step definitions and modular page objects',
        'Automated screenshot capture on failure',
        'Detailed HTML reports for traceability',
        'Efficient regression testing for release cycles'
      ],
      mediaType: 'video',
      mediaUrl: 'https://www.youtube.com/embed/kOzD034VwVI?si=gYhKE-XZJvRbr7lG', // Replace with actual image or screenshot
      category: 'qa'
    },
    {
      id: 2,
      title: 'API Automation (Robot)',
      description: 'End-to-end API automation suite using Robot Framework, Dockerized for CI/CD integration with complex BDD scenarios',
      detailedDescription: 'Designed, developed, and maintained a scalable API automation framework using Robot Framework with BDD-style syntax for complex end-to-end flows. The framework was fully Dockerized to ensure consistent execution across environments and tightly integrated into CI/CD pipelines. It supported automated test execution, dynamic test data handling, and detailed HTML/XML reporting. The solution was optimized for secure execution within AWS Cloud9 restricted environments, and tagging for selective test runs.',
      technologies: ['Robot Framework', 'Python', 'Docker', 'BDD'],
      features: [
        'BDD-style complex E2E test scenarios',
        '500+ regression and smoke test cases',
        'CI/CD integration',
        'Dockerized for consistent and portable execution',
        'Compatible with AWS Cloud9 restricted environments',
        'Logging system and reporting',
      ],
      mediaType: 'image',
      mediaUrl: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQTsJbWwk5ySFzHonrap1WhT52VmnVX7gSkLw&s',
      category: 'qa'
    },
    {
      id: 3,
      title: 'API Automation Suite (Java)',
      description: 'Java-based API automation framework using RestAssured and Cucumber with structured logging and reporting',
      detailedDescription: 'Built a robust API automation suite using RestAssured for HTTP request handling and Cucumber for BDD-style test definitions. The framework supports complex end-to-end API validation with reusable step definitions, dynamic payload generation, and test data parameterization. Integrated a custom logging system and detailed reporting in both HTML and JSON formats to enhance test traceability and debugging. Designed with modularity and scalability in mind for large regression and smoke test coverage.',
      technologies: ['Java', 'RestAssured', 'Cucumber', 'JUnit', 'Log4j'],
      features: [
        'BDD-style testing with Cucumber',
        'Modular and reusable step definitions',
        'Dynamic payload and parameterized test data',
        'Structured logging with Log4j',
        'Detailed HTML and JSON reporting',
      ],
      mediaType: 'image',
      mediaUrl: 'https://www.north-47.com/wp-content/uploads/2021/06/rest-assured-resized-copy.jpg',
      category: 'qa'
    }
  ];

  const personalProjects = [
    {
      id: 3,
      title: 'Game Automation (OpenCV)',
      description: 'Computer vision-based automation for in-game interaction',
      detailedDescription: 'Developed an automation tool leveraging OpenCV and Python to detect visual cues in real-time gameplay and simulate precise user inputs. The tool uses template matching and color thresholding to trigger automated actions based on screen feedback, significantly reducing manual interaction. Designed with modularity and efficiency for long-running sessions.',
      technologies: ['Python', 'OpenCV', 'PyAutoGUI', 'NumPy'],
      features: [
        'Real-time screen analysis',
        'Template and color-based detection',
        'Automated input simulation',
        'Lightweight and efficient execution',
        'Configurable thresholds and logic'
      ],
      mediaType: 'video',
      mediaUrl: 'https://www.youtube.com/embed/LG_yNuwVJRY?si=Cm_m6EzOUlpCnfRK',
      githubUrl: 'https://github.com/MagdyAboYoussef/AlbionAutoFisher',
      category: 'automation'
    },
    {
      id: 8,
      title: 'Gym Website (MERN)',
      description: 'Freelance web app for managing gym member access and logs',
      detailedDescription: 'Delivered a freelance project for a local gym involving a web-based system to handle member authentication, access control, and entry logging. Built using a lightweight stack, the site includes separate access levels for staff and members, real-time check-in tracking, and an admin dashboard for log reviews. Prioritized security, usability, and maintainability throughout the build.',
      technologies: ['HTML', 'CSS', 'JavaScript', 'Node.js', 'Express', 'MongoDB'],
      features: [
        'Secure member login and role-based access',
        'Real-time check-in and check-out logging',
        'Admin dashboard with log history and filters',
        'Responsive design for mobile and desktop use',
        'WhatsApp integration'
      ],
      mediaType: 'video',
      mediaUrl: 'https://www.youtube.com/embed/9ZyePADqdbE?si=toCjBHnYPmL08tXe',
      githubUrl: '',
      category: 'freelance'
    },
    {
      id: 9,
      title: 'Scraper & Player Insights',
      description: 'Java-based web scraper with automated data analysis and Excel export',
      detailedDescription: 'Built a desktop application in Java that scrapes player statistics from dynamic web pages using Selenium WebDriver. The data is parsed, analyzed to extract meaningful insights (e.g., top performers, averages), and exported to Excel using Apache POI. A simple Swing interface allows users to start scraping, view raw and processed data, and export results with one click.',
      technologies: ['Java', 'Selenium', 'Apache POI', 'Swing'],
      features: [
        'Automated web scraping with Selenium',
        'Data parsing and statistical analysis',
        'Excel export with Apache POI',
        'Swing-based desktop GUI',
        'Handles dynamic content and paginated data'
      ],
      mediaType: 'video',
      mediaUrl: 'https://www.youtube.com/embed/mTySkZousAc?si=yMdQNixvecGa6rh3',
      githubUrl: 'https://github.com/MagdyAboYoussef/ugg-simple-scraper',
      category: 'tools'
    }, {
      id: 7,
      title: 'Game Bot with OCR',
      description: 'Automated gameplay assistant using computer vision and OCR',
      detailedDescription: 'Engineered a game bot for a freelancing request that combines OpenCV for visual detection and Tesseract OCR for real-time text recognition. The bot interprets on-screen information to make context-aware decisions and perform automated tasks. Designed for extensibility, the system handles screen parsing, character status monitoring, and timed actions with minimal resource usage.',
      technologies: ['Python', 'OpenCV', 'Tesseract OCR', 'PyAutoGUI'],
      features: [
        'Text recognition via Tesseract OCR',
        'Real-time screen parsing with OpenCV',
        'Context-aware decision logic',
        'Automated mouse and keyboard interaction',
        'Highly flexible and extensible'
      ],
      mediaType: 'video',
      mediaUrl: 'https://www.youtube.com/embed/acas9LYucl4?si=iBgPioHiRF28VeYi',
      githubUrl: '',
      category: 'automation'
    },
    {
      id: 10,
      title: 'Discord WoW Manager',
      description: 'Discord bot to help communities organize and manage their members',
      detailedDescription: 'A self-hosted Discord bot that helps communities organize and manage their members.',
      technologies: ['Python', 'discord.py', 'Docker'],
      features: [
        'Self-hosted, runs 24/7'
      ],
      mediaType: 'image',
      mediaUrl: '/projects/discord-wow-manager.png',
      githubUrl: 'https://github.com/MagdyAboYoussef/World-of-warcraft-discord-raid-manager',
      liveUrl: '',
      category: 'personal'
    },
    {
      id: 11,
      title: 'CustomSpyfall',
      description: 'A social deduction game to play with friends',
      detailedDescription: 'A free, self-hosted multiplayer social game inspired by Spyfall, with support for custom locations.',
      technologies: ['Node.js', 'Express', 'Socket.IO', 'Docker'],
      features: [
        'Self-hosted, runs 24/7'
      ],
      mediaType: 'image',
      mediaUrl: '/projects/customspyfall.png',
      githubUrl: 'https://github.com/MagdyAboYoussef/CustomSpyfall',
      liveUrl: 'https://customspyfall.magdy.org',
      category: 'personal'
    },
    {
      id: 12,
      title: 'Mloha',
      description: 'Privately owned fish store website done as a side project for a friend',
      detailedDescription: 'Privately owned fish store website done as a side project for a friend.',
      technologies: ['Next.js', 'Docker'],
      features: [],
      mediaType: 'image',
      mediaUrl: '/projects/mloha.png',
      githubUrl: '',
      liveUrl: 'https://www.mloha.com/',
      category: 'personal'
    }
  ];

  const experience = [
    {
      company: 'Vodafone Intelligent Solutions (_VOIS)',
      position: 'Test Automation Engineer',
      period: 'June 2024 – Present',
      location: 'Cairo, Egypt',
      achievements: [
        'Developed comprehensive documentation explaining complex business logic and system workflows, significantly improving knowledge sharing across multiple teams',
        'Designed and maintained 500+ regression and smoke test cases, expanding coverage for new features while validating existing functionality',
        'Established multiple Postman collections for different API components, including scripts and tests run as confidence checks',
        'Engineered and maintained a Robot Framework API automation suite, integrated into CI/CD pipelines with a Dockerized image',
        'Constructed critical API automation scripts for execution in restricted AWS Cloud9 environments, enabling secure access, execution and logging',
        'Implemented a testing tool that executes complex cryptographic operations to facilitate testing',
        'Simulated IoT operations using the Raspberry Pi SDK to support hardware-involved physical device testing',
        'Implemented performance scripts in lower environments to calculate KPIs for day-to-day operations',
        'Worked with complex API protocols including gRPC and MQTT',
        'Validated and investigated data using PostgreSQL queries to confirm data integrity',
        'Integrated multiple AI workflows to accelerate automation'
      ]
    },
    {
      company: 'Logincident',
      position: 'Software Test Engineer',
      period: 'August 2023 – June 2024',
      location: 'Remote (UK)',
      achievements: [
        'Led client collaboration to define and document precise requirements, bridging stakeholders and development teams',
        'Optimized workflow efficiency by implementing management tools such as YouTrack in an Agile environment, enabling rapid response to evolving client requests',
        'Built an automation framework for iOS mobile application testing using Appium and Behave',
        'Created data generation scripts replicating client data formats, letting developers work with secure, anonymized datasets while preserving structural integrity',
        'Diagnosed complex application issues using debugging proxies across Android and iOS to intercept and analyze API traffic, producing clear bug reports',
        'Supported building dashboards from scratch according to client requirements'
      ]
    }
  ];

  const certifications = [
    {
      name: 'ISTQB Certified Tester Foundation Level (CTFL)',
      issuer: 'ISTQB',
      date: 'August 2023',
      code: '230725011',
      link: 'http://scr.istqb.org/?name=&number=230725011&orderBy=relevancy&orderDirection=&dateStart=&dateEnd=&expiryStart=&expiryEnd=&certificationBody=&examProvider=&certificationLevel=&country=&resultsPerPage=10'
    },
    {
      name: 'Certified Tester Foundation Level Agile Tester (CTFL-AT)',
      issuer: 'ISTQB',
      date: 'January 2025',
      code: '250102020',
      link: 'http://scr.istqb.org/?name=&number=250102020&orderBy=relevancy&orderDirection=&dateStart=&dateEnd=&expiryStart=&expiryEnd=&certificationBody=&examProvider=&certificationLevel=&country='
    },
    {
      name: 'CT-MAT - Certified Tester Mobile Application Tester',
      issuer: 'ISTQB',
      date: 'October 2025',
      code: '251007152',
      link: 'http://scr.istqb.org/?name=&number=251007152&orderBy=relevancy&orderDirection=&dateStart=&dateEnd=&expiryStart=&expiryEnd=&certificationBody=&examProvider=&certificationLevel=&country='
    },
    {
      name: 'Certified Tester – Testing with Generative AI (CT-Gen AI)',
      issuer: 'ISTQB',
      date: 'December 2025',
      code: '251202018',
      link: 'http://scr.istqb.org/?name=&number=251202018&orderBy=relevancy&orderDirection=&dateStart=&dateEnd=&expiryStart=&expiryEnd=&certificationBody=&examProvider=&certificationLevel=&country='
    },
    {
      name: 'Harvard CS50: Introduction to Computer Science',
      issuer: 'Harvard University',
      date: 'February 2023',
      code: null,
      link: 'https://certificates.cs50.io/c0d013af-9d05-4491-8c5e-b0b03479c217.pdf?size=letter'
    },
    {
      name: 'Software Testing Intensive Code Camp',
      issuer: 'Information Technology Institute (ITI)',
      date: 'June 2023',
      code: null,
      link: 'https://i.imgur.com/rIFTbox.png'
    }, {
      name: 'HackerRank Badges',
      issuer: 'HackerRank',
      date: 'N/A',
      code: null,
      link: 'https://www.hackerrank.com/profile/magdyaboyoussef'
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'about', 'skills', 'projects', 'experience', 'certifications', 'contact'];
      const scrollPosition = window.scrollY + 100;

      sections.forEach(section => {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isModalOpen) {
        closeProjectModal();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isModalOpen]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const openProjectModal = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const closeProjectModal = () => {
    setSelectedProject(null);
    setIsModalOpen(false);
  };

  const handleEmailChange = (e) => {
    setEmailData({
      ...emailData,
      [e.target.name]: e.target.value
    });
  };

  const sendEmail = async () => {
    setIsEmailSending(true);

    try {
      // Create mailto link with pre-filled data
      const subject = encodeURIComponent(emailData.subject || 'Portfolio Contact');
      const body = encodeURIComponent(
        `Name: ${emailData.name}\nEmail: ${emailData.email}\n\nMessage:\n${emailData.message}`
      );

      // Open default email client
      window.location.href = `mailto:magdyaboyoussef@yahoo.com?subject=${subject}&body=${body}`;

      // Reset form
      setEmailData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });

      alert('Email client opened! Please send the email from your default email application.');

    } catch (error) {
      console.error('Error sending email:', error);
      alert('Error opening email client. Please send an email directly to magdyaboyoussef@yahoo.com');
    } finally {
      setIsEmailSending(false);
    }
  };

  const openCertificationLink = (link) => {
    if (link) {
      window.open(link, '_blank', 'noopener,noreferrer');
    }
  };

  const toggleTheme = () => {
    setIsDarkTheme(!isDarkTheme);
  };

  return (
    <div className={`portfolio ${isDarkTheme ? 'dark-theme' : 'light-theme'}`}>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        :root {
          --primary: #0f172a;
          --secondary: #1e293b;
          --accent-blue: #3b82f6;
          --accent-green: #10b981;
          --text-light: #f8fafc;
          --text-muted: #64748b;
          --border: #334155;
        }

        .light-theme {
          --primary: #f1f5f9;
          --secondary: #ffffff;
          --accent-blue: #2563eb;
          --accent-green: #059669;
          --text-light: #1e293b;
          --text-muted: #475569;
          --border: #cbd5e1;
        }

        body {
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          background: var(--primary);
          color: var(--text-light);
          line-height: 1.6;
          overflow-x: hidden;
        }

        .portfolio {
          min-height: 100vh;
          background: var(--primary);
          color: var(--text-light);
        }

        /* Header */
        .header {
          position: fixed;
          top: 0;
          width: 100%;
          background: rgba(15, 23, 42, 0.95);
          backdrop-filter: blur(10px);
          z-index: 1000;
          border-bottom: 1px solid var(--border);
        }

        .light-theme .header {
          background: rgba(241, 245, 249, 0.95);
        }

        .nav {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 2rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .nav-controls {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .logo {
          font-size: 1.5rem;
          font-weight: bold;
          color: var(--accent-blue);
        }

        .theme-toggle {
          background: none;
          border: 2px solid var(--border);
          color: var(--text-light);
          border-radius: 0.5rem;
          padding: 0.5rem;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .theme-toggle:hover {
          border-color: var(--accent-blue);
          transform: translateY(-1px);
        }

        .nav-links {
          display: flex;
          list-style: none;
          gap: 2rem;
        }

        .nav-link {
          color: var(--text-light);
          text-decoration: none;
          transition: color 0.3s;
          cursor: pointer;
        }

        .nav-link:hover,
        .nav-link.active {
          color: var(--accent-blue);
        }

        .menu-toggle {
          display: none;
          background: none;
          border: none;
          color: var(--text-light);
          cursor: pointer;
        }

        /* Hero Section */
        .hero {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 2rem;
          background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
          position: relative;
          overflow: hidden;
        }

        .hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="20" cy="20" r="2" fill="%233b82f6" opacity="0.3"><animate attributeName="opacity" values="0.3;1;0.3" dur="3s" repeatCount="indefinite"/></circle><circle cx="80" cy="40" r="1.5" fill="%2310b981" opacity="0.4"><animate attributeName="opacity" values="0.4;1;0.4" dur="2s" repeatCount="indefinite"/></circle><circle cx="60" cy="80" r="2.5" fill="%233b82f6" opacity="0.2"><animate attributeName="opacity" values="0.2;0.8;0.2" dur="4s" repeatCount="indefinite"/></circle></svg>') repeat;
        }

        .hero-content {
          position: relative;
          z-index: 1;
          max-width: 800px;
          animation: fadeInUp 1s ease-out;
        }

        .hero h1 {
          font-size: clamp(2.5rem, 5vw, 4rem);
          margin-bottom: 1rem;
          background: linear-gradient(135deg, var(--accent-blue), var(--accent-green));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero p {
          font-size: 1.25rem;
          color: var(--text-muted);
          margin-bottom: 2rem;
        }

        .cta-buttons {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .btn {
          padding: 0.75rem 1.5rem;
          border: none;
          border-radius: 0.5rem;
          font-weight: 600;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.3s;
          cursor: pointer;
        }

        .btn-primary {
          background: var(--accent-blue);
          color: white;
        }

        .btn-secondary {
          background: transparent;
          color: var(--text-light);
          border: 2px solid var(--accent-blue);
        }

        .btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(59, 130, 246, 0.3);
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        /* Section Styles */
        .section {
          padding: 5rem 2rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .section h2 {
          font-size: 2.5rem;
          text-align: center;
          margin-bottom: 3rem;
          position: relative;
        }

        .section h2::after {
          content: '';
          position: absolute;
          bottom: -0.5rem;
          left: 50%;
          transform: translateX(-50%);
          width: 4rem;
          height: 0.25rem;
          background: var(--accent-blue);
          border-radius: 0.125rem;
        }

        /* About Section */
        .about-content {
          display: grid;
          grid-template-columns: 1fr 2fr;
          gap: 3rem;
          align-items: center;
        }

        .about-image {
          width: 100%;
          max-width: 300px;
          height: 300px;
          border-radius: 50%;
          background: #f3f4f6;
          border: 4px solid var(--accent-blue);
          overflow: hidden;
          margin: 0 auto;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }

        .about-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .about-image-placeholder {
          font-size: 1rem;
          color: var(--text-muted);
          text-align: center;
          padding: 1rem;
        }

        .about-text {
          font-size: 1.1rem;
          line-height: 1.8;
        }

        .about-text p {
          margin-bottom: 1.5rem;
        }

        .highlights {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-top: 2rem;
        }

        .highlight {
          background: var(--secondary);
          padding: 1rem;
          border-radius: 0.5rem;
          border-left: 4px solid var(--accent-blue);
        }

        .highlight h4 {
          color: var(--accent-blue);
          margin-bottom: 0.5rem;
        }

        /* Skills Section */
        .skills-categories {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
          margin-top: 2rem;
          align-items: stretch;
        }

        .skill-category {
          background: var(--secondary);
          padding: 1.5rem;
          border-radius: 0.75rem;
          border-top: 4px solid var(--accent-blue);
          display: flex;
          flex-direction: column;
          height: 100%;
          min-height: 400px;
        }

        .skill-category h3 {
          margin-bottom: 1.5rem;
          color: var(--accent-blue);
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 1.1rem;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .skills-list {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .skill-item {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .skill-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .skill-name {
          font-size: 0.95rem;
          font-weight: 500;
          color: var(--text-light);
        }

        .skill-percentage {
          font-size: 0.85rem;
          color: var(--text-muted);
          font-weight: 600;
          min-width: 35px;
          text-align: right;
        }

        .skill-bar {
          width: 100%;
          height: 0.5rem;
          background: var(--border);
          border-radius: 0.25rem;
          overflow: hidden;
        }

        .skill-progress {
          height: 100%;
          background: #3b82f6; /* Fallback solid color */
          background: -webkit-linear-gradient(90deg, #3b82f6, #10b981); /* Safari/Chrome */
          background: -moz-linear-gradient(90deg, #3b82f6, #10b981); /* Firefox */
          background: linear-gradient(90deg, #3b82f6, #10b981); /* Standard */
          background: linear-gradient(90deg, var(--accent-blue), var(--accent-green)) !important;
          border-radius: 0.25rem;
          transition: width 1s ease-in-out;
          -webkit-border-radius: 0.25rem; /* Safari */
        }

        /* Projects Section */
        .projects-filter {
          display: flex;
          justify-content: center;
          gap: 1rem;
          margin-bottom: 3rem;
          flex-wrap: wrap;
        }

        .filter-btn {
          padding: 0.5rem 1rem;
          background: transparent;
          border: 2px solid var(--border);
          color: var(--text-light);
          border-radius: 0.5rem;
          cursor: pointer;
          transition: all 0.3s;
        }

        .filter-btn.active {
          border-color: var(--accent-blue);
          background: var(--accent-blue);
        }

        .projects-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 2rem;
        }

        .project-card {
          background: var(--secondary);
          border-radius: 0.75rem;
          overflow: hidden;
          transition: transform 0.3s, box-shadow 0.3s;
          cursor: pointer;
        }

        .project-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
        }

        .light-theme .project-card:hover {
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
        }

        .project-content {
          padding: 1.5rem;
        }

        .project-title {
          font-size: 1.25rem;
          margin-bottom: 0.5rem;
          color: var(--accent-blue);
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .project-description {
          color: var(--text-muted);
          margin-bottom: 1rem;
        }

        .project-tech {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 1rem;
        }

        .tech-tag {
          background: var(--accent-blue);
          color: white;
          padding: 0.25rem 0.5rem;
          border-radius: 0.25rem;
          font-size: 0.8rem;
        }

        .project-features {
          list-style: none;
        }

        .project-features li {
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
          font-size: 0.9rem;
        }

        .project-features li::before {
          content: '✓';
          color: var(--accent-green);
          font-weight: bold;
          flex-shrink: 0;
          margin-top: 0.1rem;
        }

        .project-actions {
          display: flex;
          gap: 0.75rem;
          margin-top: 1rem;
          flex-wrap: wrap;
        }

        .project-btn {
          padding: 0.5rem 1rem;
          border: none;
          border-radius: 0.375rem;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.3s;
        }

        .project-btn-primary {
          background: var(--accent-blue);
          color: white;
        }

        .project-btn-secondary {
          background: var(--accent-green);
          color: white;
        }

        .project-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }

        .light-theme .project-btn:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          backdrop-filter: blur(4px);
          z-index: 2000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          animation: fadeIn 0.3s ease-out;
        }

        .modal-content {
          background: var(--secondary);
          border-radius: 1rem;
          max-width: 800px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
          animation: slideIn 0.3s ease-out;
        }

        .modal-header {
          padding: 1.5rem;
          border-bottom: 1px solid var(--border);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-title {
          font-size: 1.5rem;
          font-weight: bold;
          color: var(--accent-blue);
        }

        .modal-close {
          background: none;
          border: none;
          color: var(--text-light);
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 0.25rem;
          transition: background 0.3s;
        }

        .modal-close:hover {
          background: var(--border);
        }

        .modal-body {
          padding: 1.5rem;
        }

        .modal-media {
          width: 100%;
          border-radius: 0.5rem;
          margin-bottom: 1.5rem;
        }

        .modal-video {
          height: 300px;
        }

        .modal-image {
          max-height: 450px;
          object-fit: contain;
          background: var(--secondary);
        }

        .modal-description {
          font-size: 1rem;
          line-height: 1.6;
          color: var(--text-light);
          margin-bottom: 1.5rem;
        }

        .modal-tech {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 1.5rem;
        }

        .modal-features {
          list-style: none;
        }

        .modal-features li {
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
          margin-bottom: 0.75rem;
          font-size: 0.95rem;
        }

        .modal-features li::before {
          content: '▸';
          color: var(--accent-green);
          font-weight: bold;
          flex-shrink: 0;
          margin-top: 0.1rem;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideIn {
          from { 
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        /* Experience Section */
        .experience-timeline {
          position: relative;
          margin-left: 2rem;
        }

        .experience-timeline::before {
          content: '';
          position: absolute;
          left: -1rem;
          top: 0;
          bottom: 0;
          width: 2px;
          background: var(--accent-blue);
        }

        .experience-item {
          position: relative;
          background: var(--secondary);
          padding: 1.5rem;
          border-radius: 0.75rem;
          margin-bottom: 2rem;
          margin-left: 1rem;
        }

        .experience-item::before {
          content: '';
          position: absolute;
          left: -2.25rem;
          top: 1.5rem;
          width: 1rem;
          height: 1rem;
          background: var(--accent-blue);
          border-radius: 50%;
          border: 3px solid var(--primary);
        }

        .experience-header {
          margin-bottom: 1rem;
        }

        .experience-company {
          font-size: 1.25rem;
          color: var(--accent-blue);
          font-weight: bold;
        }

        .experience-position {
          font-size: 1.1rem;
          margin: 0.25rem 0;
        }

        .experience-meta {
          display: flex;
          gap: 1rem;
          color: var(--text-muted);
          font-size: 0.9rem;
          flex-wrap: wrap;
        }

        .experience-achievements {
          list-style: none;
        }

        .experience-achievements li {
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
          margin-bottom: 0.75rem;
        }

        .experience-achievements li::before {
          content: '▸';
          color: var(--accent-green);
          font-weight: bold;
          flex-shrink: 0;
          margin-top: 0.1rem;
        }

        /* Certifications Section */
        .certifications-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
        }

        .certification-card {
          background: var(--secondary);
          padding: 1.5rem;
          border-radius: 0.75rem;
          border-left: 4px solid var(--accent-blue);
          transition: transform 0.3s, box-shadow 0.3s;
          cursor: pointer;
        }

        .certification-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 25px rgba(59, 130, 246, 0.2);
        }

        .certification-name {
          font-size: 1.1rem;
          font-weight: bold;
          margin-bottom: 0.5rem;
          color: var(--accent-blue);
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .certification-issuer {
          color: var(--text-muted);
          margin-bottom: 0.5rem;
        }

        .certification-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.9rem;
          color: var(--text-muted);
        }

        /* Contact Section */
        .contact-content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 3rem;
          align-items: start;
        }

        .contact-info {
          space-y: 1.5rem;
        }

        .contact-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .contact-item svg {
          color: var(--accent-blue);
        }

        .contact-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-group label {
          font-weight: 600;
          color: var(--text-light);
        }

        .form-group input,
        .form-group textarea {
          padding: 0.75rem;
          border: 1px solid var(--border);
          border-radius: 0.5rem;
          background: var(--secondary);
          color: var(--text-light);
          font-family: inherit;
          transition: border-color 0.3s;
        }

        .form-group input:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: var(--accent-blue);
        }

        .form-group textarea {
          resize: vertical;
          min-height: 120px;
        }

        /* Footer */
        .footer {
          background: var(--secondary);
          padding: 2rem;
          text-align: center;
          border-top: 1px solid var(--border);
        }

        .social-links {
          display: flex;
          justify-content: center;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .social-link {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 3rem;
          height: 3rem;
          background: var(--primary);
          color: var(--text-light);
          border-radius: 50%;
          text-decoration: none;
          transition: all 0.3s;
        }

        .social-link:hover {
          background: var(--accent-blue);
          transform: translateY(-2px);
        }

        /* Animations */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .fade-in-up {
          animation: fadeInUp 0.6s ease-out;
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .nav-links {
            display: none;
          }

          .menu-toggle {
            display: block;
          }

            .nav-links.mobile-open {
            display: flex;
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            background: var(--primary);
            flex-direction: column;
            padding: 1rem;
            border-top: 1px solid var(--border);
          }

          .light-theme .nav-links.mobile-open {
            background: var(--primary);
          }

          /* Additional light theme fixes */
          .light-theme .about-image {
            background: var(--secondary);
          }

          .light-theme .experience-item::before {
            border: 3px solid var(--primary);
          }

          /* Ensure all text uses proper colors in light theme */
          .light-theme,
          .light-theme * {
            color: inherit;
          }

          .light-theme .hero p,
          .light-theme .section p,
          .light-theme .about-text p {
            color: var(--text-light);
          }

          /* Fix project descriptions and other muted text in light theme */
          .light-theme .project-description,
          .light-theme .experience-meta,
          .light-theme .certification-issuer,
          .light-theme .certification-meta {
            color: var(--text-muted);
          }

          .nav-controls {
            order: 1;
          }

          .nav-links {
            order: 2;
          }

          .menu-toggle {
            order: 3;
          }

          .about-content {
            grid-template-columns: 1fr;
            text-align: center;
          }

          .contact-content {
            grid-template-columns: 1fr;
          }

          .experience-timeline {
            margin-left: 0;
          }

          .experience-timeline::before {
            display: none;
          }

          .experience-item {
            margin-left: 0;
          }

          .experience-item::before {
            display: none;
          }

          .cta-buttons {
            flex-direction: column;
            align-items: center;
          }

          .skills-categories {
            grid-template-columns: 1fr;
          }

          .projects-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 480px) {
          .nav {
            padding: 1rem;
          }

          .section {
            padding: 3rem 1rem;
          }

          .hero {
            padding: 1rem;
          }

          .projects-filter {
            flex-direction: column;
            align-items: center;
          }

          .project-actions {
            flex-direction: column;
          }

          .project-btn {
            justify-content: center;
          }

          .modal-content {
            margin: 0.5rem;
          }

          .modal-header,
          .modal-body {
            padding: 1rem;
          }

          .modal-video {
            height: 200px;
          }
        }
      `}</style>

      {/* Header */}
      <header className="header">
        <nav className="nav">
          <div className="logo">Magdy Abo Youssef</div>
          <ul className={`nav-links ${isMenuOpen ? 'mobile-open' : ''}`}>
            {['home', 'about', 'skills', 'projects', 'experience', 'certifications', 'contact'].map(section => (
              <li key={section}>
                <a
                  href={`#${section}`}
                  className={`nav-link ${activeSection === section ? 'active' : ''}`}
                  onClick={(e) => { e.preventDefault(); scrollToSection(section); }}
                >
                  {section.charAt(0).toUpperCase() + section.slice(1)}
                </a>
              </li>
            ))}
          </ul>
          <div className="nav-controls">
            <button 
              className="theme-toggle"
              onClick={toggleTheme}
              title={`Switch to ${isDarkTheme ? 'light' : 'dark'} theme`}
            >
              {isDarkTheme ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <button
              className="menu-toggle"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="hero-content">
          <h1>QA & Test Automation Engineer</h1>
          <p>ISTQB & Agile Certified | Test Automation Engineer | Quality Assurance Expert</p>
          <div className="cta-buttons">
            <button className="btn btn-primary" onClick={() => scrollToSection('projects')}>
              <TestTube size={20} />
              View QA Projects
            </button>
            <a className="btn btn-secondary" href="/MagdyAboYoussef.pdf" download="Magdy_Abo_Youssef_CV.pdf">              <Download size={20} />
              Download CV
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section">
        <h2>About Me</h2>
        <div className="about-content">
          <div className="about-image">
            {/* Replace the src with your actual photo */}
            <img
              src="profile.jpg"
              alt="Magdy Abo Youssef"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <div className="about-image-placeholder" style={{ display: 'none' }}>
              Placeholder<br />
              <small>(300x300px recommended)</small>
            </div>
          </div>
          <div className="about-text">
            <p>
              QA Engineer with over three years of experience in test automation and manual testing,
              specializing in end-to-end quality assurance across APIs, databases, and frontend. Experienced
              with complex systems including IoT devices, various API protocols, and performance testing.
            </p>
            <p>
              I stay current with industry trends, AI technologies, and testing frameworks to maintain
              a competitive edge and deliver value. Passionate about building reliable automation solutions
              that save time and improve software quality.
            </p>
            <div className="highlights">
              <div className="highlight">
                <h4>500+</h4>
                <p>Test Cases Designed & Maintained</p>
              </div>
              <div className="highlight">
                <h4>ISTQB</h4>
                <p>Foundation, Agile, Mobile & AI Certified</p>
              </div>
              <div className="highlight">
                <h4>IoT & Hardware</h4>
                <p>Device testing with Raspberry Pi SDK</p>
              </div>
              <div className="highlight">
                <h4>Performance</h4>
                <p>Load testing with Grafana k6</p>
              </div>
              <div className="highlight">
                <h4>Business Oriented</h4>
                <p>Understands complex business logic & edge cases</p>
              </div>
              <div className="highlight">
                <h4>API Protocols</h4>
                <p>REST, gRPC, MQTT</p>
              </div>
              <div className="highlight">
                <h4>Automation Driven</h4>
                <p>Delivers value & saves time through automation</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="section">
        <h2>Technical Skills</h2>
        <div className="skills-categories">
          <div className="skill-category">
            <h3><TestTube size={24} />Quality Assurance</h3>
            {skills.qa.map((skill, index) => (
              <div key={index} className="skill-item">
                <span>{skill.name}</span>
                <div className="skill-bar">
                  <div
                    className="skill-progress"
                    style={{ width: `${skill.level}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>

          <div className="skill-category">
            <h3><Code size={24} />Automation Tools</h3>
            {skills.automation.map((skill, index) => (
              <div key={index} className="skill-item">
                <span>{skill.name}</span>
                <div className="skill-bar">
                  <div
                    className="skill-progress"
                    style={{ width: `${skill.level}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>

          <div className="skill-category">
            <h3><Code size={24} />Programming</h3>
            {skills.programming.map((skill, index) => (
              <div key={index} className="skill-item">
                <span>{skill.name}</span>
                <div className="skill-bar">
                  <div
                    className="skill-progress"
                    style={{ width: `${skill.level}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>

          <div className="skill-category">
            <h3><Database size={24} />DevOps & Tools</h3>
            {skills.devops.map((skill, index) => (
              <div key={index} className="skill-item">
                <span>{skill.name}</span>
                <div className="skill-bar">
                  <div
                    className="skill-progress"
                    style={{ width: `${skill.level}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
          <div className="skill-category">
            <h3><Database size={24} />Web development (General)</h3>
            {skills.general.map((skill, index) => (
              <div key={index} className="skill-item">
                <span>{skill.name}</span>
                <div className="skill-bar">
                  <div
                    className="skill-progress"
                    style={{ width: `${skill.level}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="section">
        <h2>Projects Portfolio</h2>

        {/* QA Projects */}
        <div style={{ marginBottom: '4rem' }}>
          <h3 style={{
            fontSize: '2rem',
            color: '#3b82f6',
            textAlign: 'center',
            marginBottom: '2rem'
          }}>
            QA & Automation Projects
          </h3>
          <div className="projects-grid">
            {qaProjects.map(project => (
              <div key={project.id} className="project-card">
                <div className="project-content">
                  <h4 className="project-title">
                    {project.mediaType === 'video' ? <Video size={20} /> : <Image size={20} />}
                    {project.title}
                  </h4>
                  <p className="project-description">{project.description}</p>
                  <div className="project-tech">
                    {project.technologies.map((tech, index) => (
                      <span key={index} className="tech-tag">{tech}</span>
                    ))}
                  </div>
                  <ul className="project-features">
                    {project.features.slice(0, 3).map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                    {project.features.length > 3 && (
                      <li style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                        +{project.features.length - 3} more features...
                      </li>
                    )}
                  </ul>
                  <div className="project-actions">
                    <button
                      className="project-btn project-btn-primary"
                      onClick={() => openProjectModal(project)}
                    >
                      <ExternalLink size={16} />
                      View Demo
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Personal Projects */}
        <div>
          <h3 style={{
            fontSize: '2rem',
            color: '#10b981',
            textAlign: 'center',
            marginBottom: '2rem'
          }}>
            Personal Hobby & Freelance Projects
          </h3>
          <div className="projects-grid">
            {personalProjects.map(project => (
              <div key={project.id} className="project-card">
                <div className="project-content">
                  <h4 className="project-title" style={{ color: '#10b981' }}>
                    {project.mediaType === 'video' ? <Video size={20} /> : <Image size={20} />}
                    {project.title}
                  </h4>
                  <p className="project-description">{project.description}</p>
                  <div className="project-tech">
                    {project.technologies.map((tech, index) => (
                      <span
                        key={index}
                        className="tech-tag"
                        style={{ background: '#10b981' }}
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                  <ul className="project-features">
                    {project.features.slice(0, 3).map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                    {project.features.length > 3 && (
                      <li style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                        +{project.features.length - 3} more features...
                      </li>
                    )}
                  </ul>
<div className="project-actions">
  <button 
    className="project-btn project-btn-primary"
    onClick={() => openProjectModal(project)}
  >
    <ExternalLink size={16} />
    View Details
  </button>
  {project.githubUrl && (
    <a 
      href={project.githubUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="project-btn project-btn-secondary"
    >
      <Github size={16} />
      Show Code
    </a>
  )}
  {project.liveUrl && (
    <a
      href={project.liveUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="project-btn project-btn-secondary"
    >
      <Globe size={16} />
      Visit Site
    </a>
  )}
</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="section">
        <h2>Professional Experience</h2>
        <div className="experience-timeline">
          {experience.map((job, index) => (
            <div key={index} className="experience-item">
              <div className="experience-header">
                <div className="experience-company">{job.company}</div>
                <div className="experience-position">{job.position}</div>
                <div className="experience-meta">
                  <span><Calendar size={16} /> {job.period}</span>
                  <span><MapPin size={16} /> {job.location}</span>
                </div>
              </div>
              <ul className="experience-achievements">
                {job.achievements.map((achievement, achievementIndex) => (
                  <li key={achievementIndex}>{achievement}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Certifications Section */}
      <section id="certifications" className="section">
        <h2>Certifications & Education</h2>
        <div className="certifications-grid">
          {certifications.map((cert, index) => (
            <div
              key={index}
              className="certification-card"
              onClick={() => openCertificationLink(cert.link)}
              title="Click to view certification details"
            >
              <div className="certification-name">
                <ExternalLink size={16} />
                {cert.name}
              </div>
              <div className="certification-issuer">{cert.issuer}</div>
              <div className="certification-meta">
                <span>{cert.date}</span>
                {cert.code && <span>Code: {cert.code}</span>}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section">
        <h2>Get In Touch</h2>
        <div className="contact-content">
          <div className="contact-info">
            <div className="contact-item">
              <Mail size={24} />
              <div>
                <div>MagdyAboYoussef@yahoo.com</div>
              </div>
            </div>
            <div className="contact-item">
              <Phone size={24} />
              <div>
                <div>+20 111 193 3565</div>
              </div>
            </div>
            <div className="contact-item">
              <MapPin size={24} />
              <div>
                <div>6th of October, Cairo, Egypt</div>
              </div>
            </div>
            <div className="contact-item">
              <Linkedin size={24} />
              <div>
                <div>linkedin.com/in/magdyaboyoussef</div>
              </div>
            </div>
          </div>

          <div className="contact-form">
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                name="name"
                value={emailData.name}
                onChange={handleEmailChange}
                placeholder="Your Name"
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={emailData.email}
                onChange={handleEmailChange}
                placeholder="your.email@example.com"
              />
            </div>
            <div className="form-group">
              <label>Subject</label>
              <input
                type="text"
                name="subject"
                value={emailData.subject}
                onChange={handleEmailChange}
                placeholder="Project Inquiry"
              />
            </div>
            <div className="form-group">
              <label>Message</label>
              <textarea
                name="message"
                value={emailData.message}
                onChange={handleEmailChange}
                placeholder="Tell me about your project or opportunity..."
              ></textarea>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              disabled={isEmailSending}
              onClick={sendEmail}
            >
              {isEmailSending ? (
                <Send size={20} style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <Mail size={20} />
              )}
              {isEmailSending ? 'Opening Email Client...' : 'Send Message'}
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="social-links">
          <a href="https://github.com/MagdyAboYoussef" className="social-link">
            <Github size={20} />
          </a>
          <a href="https://www.linkedin.com/in/magdyaboyoussef/" className="social-link">
            <Linkedin size={20} />
          </a>
          <a href="mailto:MagdyAboYoussef@yahoo.com" className="social-link">
            <Mail size={20} />
          </a>
        </div>
        <p>&copy; 2025 Magdy Abo Youssef. All rights reserved.</p>
      </footer>

      {/* Project Modal */}
      {isModalOpen && selectedProject && (
        <div className="modal-overlay" onClick={closeProjectModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{selectedProject.title}</h3>
              <button className="modal-close" onClick={closeProjectModal}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              {/* Media Demo Section */}
              {selectedProject.mediaUrl && (
                <div>
                  <h4 style={{ marginBottom: '1rem', color: 'var(--accent-blue)' }}>
                    {selectedProject.mediaType === 'video' ? '🎥 Project Demo' : '📸 Project Preview'}
                  </h4>
                  {selectedProject.mediaType === 'video' ? (
                    <iframe
                      className="modal-media modal-video"
                      src={selectedProject.mediaUrl}
                      title={`${selectedProject.title} Demo`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  ) : (
                    <div>
                      <img
                        className="modal-media modal-image"
                        src={selectedProject.mediaUrl}
                        alt={`${selectedProject.title} Preview`}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div
                        style={{
                          display: 'none',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '300px',
                          background: 'var(--border)',
                          borderRadius: '0.5rem',
                          color: 'var(--text-muted)',
                          marginBottom: '1.5rem'
                        }}
                      >
                        <div style={{ textAlign: 'center' }}>
                          <Image size={48} style={{ marginBottom: '1rem' }} />
                          <p>Image preview not available</p>
                          <small>Replace with actual project image</small>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Project Description */}
              <div>
                <h4 style={{ marginBottom: '1rem', color: 'var(--accent-blue)' }}>
                  📋 Detailed Description
                </h4>
                <p className="modal-description">{selectedProject.detailedDescription}</p>
              </div>

              {/* Technologies */}
              <div>
                <h4 style={{ marginBottom: '1rem', color: 'var(--accent-blue)' }}>
                  🛠️ Technologies Used
                </h4>
                <div className="modal-tech">
                  {selectedProject.technologies.map((tech, index) => (
                    <span
                      key={index}
                      className="tech-tag"
                      style={{
                        background: selectedProject.category === 'qa' ? 'var(--accent-blue)' : 'var(--accent-green)'
                      }}
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              {/* Features */}
              {selectedProject.features.length > 0 && (
                <div>
                  <h4 style={{ marginBottom: '1rem', color: 'var(--accent-blue)' }}>
                    ✨ Key Features
                  </h4>
                  <ul className="modal-features">
                    {selectedProject.features.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* GitHub / Live Site Links */}
              {(selectedProject.githubUrl || selectedProject.liveUrl) && (
                <div style={{ marginTop: '1.5rem', textAlign: 'center', display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                  {selectedProject.githubUrl && (
                    <a
                      href={selectedProject.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                      style={{ background: 'var(--accent-green)' }}
                    >
                      <Github size={20} />
                      View on GitHub
                    </a>
                  )}
                  {selectedProject.liveUrl && (
                    <a
                      href={selectedProject.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                      style={{ background: 'var(--accent-green)' }}
                    >
                      <Globe size={20} />
                      Visit Site
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Portfolio;
